<template>
  <div :class="rootClasses">
    <div v-if="showHeaderSection" class="bitts-table-pro__cta-wrapper">
      <div class="bitts-table-pro__cta-left">
        <slot name="cta-left">
          <div
            :class="shrinkTitle ? null : 'flex-1'"
            class="text-neutral-900 text-lg"
          >
            {{ title }}
          </div>
        </slot>
      </div>
      <div class="bitts-table-pro__cta-right">
        <slot name="cta-right" />
      </div>
    </div>
    <div
      class="bitts-table-pro__ag-grid-container"
      :class="{
        'bitts-table-pro__empty': noRows,
        'ph-no-capture': hideFromSessionReplay,
      }"
    >
      <AgGridVue
        :cache-block-size="isServerSide ? paginationPageSize : undefined"
        :column-defs="columns"
        :default-col-def="mergedColumnDef"
        :row-data="isServerSide ? undefined : rows"
        :enable-cell-text-selection="enableCellTextSelection"
        loading-cell-renderer="bittsRowLoader"
        :max-blocks-in-cache="0"
        no-rows-overlay-component="bittsEmptyState"
        :no-rows-overlay-component-params="{
          title: noRowsProps.title,
          description: noRowsProps.description,
          descriptionHtml: noRowsProps.descriptionHtml,
          icon: noRowsProps.icon,
        }"
        :pagination="pagination"
        :suppress-pagination-panel="hidePagination"
        :pagination-page-size="paginationPageSize"
        :pagination-page-size-selector="paginationPageSizeSelector"
        :suppress-cell-focus="suppressCellFocus"
        :suppress-row-hover-highlight="suppressRowHoverHighlight"
        :suppress-drag-leave-hides-columns="suppressDragLeaveHidesColumns"
        :suppress-click-edit="true"
        :sorting-order="sortOrder"
        :row-selection="rowSelection"
        :row-height="rowHeight"
        :server-side-datasource="dataSource"
        :row-model-type="rowModelType"
        :server-side-initial-row-count="0"
        :suppress-movable-columns="suppressMovableColumns"
        :components="components"
        :context="context"
        :get-row-id="!!rowId.length ? getRowId : undefined"
        class="ag-theme-balham"
        :dom-layout="domLayout"
        :always-show-horizontal-scroll="alwaysShowHorizontalScroll"
        v-on="extraHandlers"
        @cell-clicked="onCellClicked"
        @row-clicked="onRowClicked"
        @cell-key-down="onCellKeyDown"
        @column-moved="onColumnMoved"
        @first-data-rendered="onDataRendered"
        @grid-size-changed="onDataRendered"
        @grid-ready="onGridReady"
        @pagination-changed="onPaginationChanged"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Nullable } from '@crossbeam/types';

import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import {
  CellClickedEvent,
  CellKeyDownEvent,
  ColDef,
  ColumnMovedEvent,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  ModuleRegistry,
  RowClickedEvent,
} from '@ag-grid-community/core';
import { AgGridVue } from '@ag-grid-community/vue3';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import {
  Slots,
  computed,
  defineComponent,
  onBeforeMount,
  ref,
  shallowRef,
  useSlots,
  watch,
} from 'vue';

import { AG_GRID_KEY } from './bitts-table-consts';
import BittsTableProEmptyState from './BittsTableProEmptyState.vue';
import BittsTableProRowLoader from './BittsTableProRowLoader.vue';
import {
  suppressCellKeyboardEvent,
  suppressHeaderCellKeyboardEvent,
} from './suppressKeyboardEvent';
import {
  BittsTablePropsClientSide,
  BittsTablePropsServerSide,
  PassedContext,
} from './types';

import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-balham.css';

const {
  alwaysShowHorizontalScroll = true,
  columns = [],
  compressColumns = false,
  dataSource = null,
  defaultColumnDef = {},
  domLayout = 'autoHeight',
  enforceResponsiveSizing = false,
  enableCellTextSelection = false,
  hideFromSessionReplay = true,
  noRows = false,
  noRowsProps = {
    title: 'No results',
    description:
      'Try a different search term to find accounts or opportunities',
    icon: ['far', 'search'],
  },
  onRowSelected = null,
  pagination = true,
  paginationPageSize = 20,
  paginationPageSizeSelector = false,
  hidePagination = false,
  passedContext = {},
  rowHeight,
  rowModelType = 'clientSide',
  rowSelection = null,
  rows = [],
  rowId = '',
  searchQuery = '',
  shrinkTitle = false,
  sortOrder = ['asc', 'desc'],
  stickyPagination = false,
  stickyHeaders = false,
  stickyHeaderOffsetHeight = '345px',
  suppressCellFocus = false,
  suppressRowHoverHighlight = true,
  suppressDragLeaveHidesColumns = true,
  suppressMovableColumns = false,
  title = '',
  useSearch = false,
  zebra = false,
} = defineProps<BittsTablePropsClientSide | BittsTablePropsServerSide>();

const emit = defineEmits<{
  (e: 'pageClick', page: number): void;
  (e: 'grid-ready', params: GridReadyEvent): void;
  (e: 'columns-compressed'): void;
  (e: 'cell-clicked', data: CellClickedEvent): void;
  (e: 'row-clicked', data: RowClickedEvent['data']): void;
  (e: 'column-moved', data: ColumnMovedEvent): void;
}>();

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ServerSideRowModelModule,
]);

LicenseManager.setLicenseKey(AG_GRID_KEY);

const mergedColumnDef = computed<ColDef>(() => ({
  suppressKeyboardEvent: suppressCellKeyboardEvent,
  suppressHeaderKeyboardEvent: suppressHeaderCellKeyboardEvent,
  ...defaultColumnDef,
}));

const isServerSide = computed(() => rowModelType === 'serverSide');

const page = ref<number>(1);
const lastPage = ref<Nullable<number>>(null);

function onPaginationChanged() {
  if (
    !gridApi.value?.paginationGetCurrentPage() &&
    gridApi.value?.paginationGetCurrentPage() !== 0
  )
    return;
  page.value = gridApi.value?.paginationGetCurrentPage();
  lastPage.value = gridApi.value?.paginationGetTotalPages();
  emit('pageClick', page.value);
}

const gridApi = shallowRef<Nullable<GridApi>>(null);

const slots: Slots = useSlots();
const showHeaderSection = computed(() => title || !!slots['cta-left']);

type Component = ReturnType<typeof defineComponent>;
const context = ref<Nullable<PassedContext>>(null);
const components = ref<Nullable<Record<string, Component>>>(null);
onBeforeMount(() => {
  context.value = passedContext;
  components.value = {
    bittsEmptyState: BittsTableProEmptyState,
    bittsRowLoader: BittsTableProRowLoader,
  };
});

const rootClasses = computed(() => {
  return {
    'bitts-table-pro': true,
    'bitts-table-pro__sticky-pagination': stickyPagination,
    'bitts-table-pro__sticky-headers': stickyHeaders,
    'bitts-table-pro__zebra': zebra,
    'bitts-table-pro__fill-height': domLayout === 'normal',
    'bitts-table-pro__no-page-size-selector':
      !paginationPageSizeSelector && pagination,
    'with-header': showHeaderSection.value,
  };
});
const extraHandlers = computed(() => {
  if (!onRowSelected) return {};
  return { 'row-selected': onRowSelected };
});

// TODO: filter instead of find?
function buildResponsiveGrid(params: FirstDataRenderedEvent) {
  const flexColumn = params.api
    .getColumns()
    ?.find((col) => col.getColDef().flex);

  if (flexColumn) {
    const gridPixelRange =
      document.querySelector('.bitts-table-pro')?.getBoundingClientRect()
        .width || 0;
    const totalColumnWidths =
      params.api
        .getColumns()
        ?.reduce((sum, col) => sum + col.getActualWidth(), 0) ?? 0;
    if (gridPixelRange >= totalColumnWidths) {
      params.api.sizeColumnsToFit();
    } else {
      // if (params.type !== 'gridSizeChanged') params.api.autoSizeAllColumns();
      params.api.autoSizeColumns([flexColumn.getColId()]);
    }
  }
}
function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;

  lastPage.value = gridApi.value?.paginationGetTotalPages() || null;
  emit('grid-ready', params);
}

function onDataRendered(params: FirstDataRenderedEvent) {
  if (enforceResponsiveSizing) buildResponsiveGrid(params);
  else if (compressColumns) params.api.sizeColumnsToFit();
}

function onCellClicked(e: CellClickedEvent) {
  emit('cell-clicked', e);
}
function onRowClicked(e: RowClickedEvent) {
  emit('row-clicked', e.data);
}
function onCellKeyDown(e: CellKeyDownEvent) {
  if (!(e.event instanceof KeyboardEvent) || e.event.key !== 'Enter') return;
  const target = e.event.target;
  if (!(target instanceof HTMLElement) || !target.classList.contains('ag-cell'))
    return;
  emit('row-clicked', e.data);
}
function onColumnMoved(e: ColumnMovedEvent) {
  emit('column-moved', e);
}

function getRowId(params: { data: Record<string, unknown> }) {
  if (!rowId.length) return;
  return String(params.data[rowId]);
}

watch(
  () => searchQuery,
  (query) => {
    if (!useSearch) return;
    gridApi.value?.setGridOption('quickFilterText', query);
  },
);

defineExpose({ gridApi });
</script>

<style lang="pcss" scoped>
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-table-pro:deep() {
  .ag-cell {
    @apply text-neutral-text;
    --ag-range-selection-border-color: transparent;
  }
  .ag-cell:focus-visible,
  .ag-header-cell:focus-visible {
    @apply rounded-4 outline outline-2 -outline-offset-2 outline-neutral-border-focus;
  }
  .ag-header,
  .ag-row {
    @apply bg-white border-neutral-border;
  }
  .ag-header-cell-text {
    @apply text-neutral-text-weak;
  }
  .ag-header-cell::after,
  .ag-header-group-cell::after {
    @apply hidden;
  }

  .ag-layout-auto-height .ag-center-cols-clipper,
  .ag-layout-auto-height .ag-center-cols-container {
    min-height: v-bind(rowHeight);
  }

  .ag-paging-button {
    @apply text-neutral-accent p-4 py-2 border-l border-solid border-neutral-border m-0;
    &:first-child {
      @apply border-l-0;
    }
    &.ag-disabled {
      @apply cursor-not-allowed;
    }
  }
  .ag-paging-description {
    @apply border-l border-solid border-neutral-border self-stretch flex items-center gap-2 m-0 px-8;
  }
  .ag-paging-page-summary-panel {
    @apply border border-solid border-neutral-border rounded-bts-sm px-4;
  }
  .ag-paging-panel {
    @apply flex bg-white justify-between items-center text-neutral-text font-bold;
  }

  .ag-pinned-left-header,
  .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right):not(
      .ag-cell-range-single-cell
    ) {
    @apply border-r-0;
  }
  .ag-cell-value {
    @apply flex items-center text-neutral-text;
  }
  .ag-root-wrapper {
    @apply rounded-2xl border-neutral-border;
  }
  .ag-row-last {
    @apply border-b-0;
  }
  .ag-row-hover:not(.ag-full-width-row)::before,
  .ag-row-hover.ag-full-width-row.ag-row-group::before {
    @apply bg-neutral-background-weak;
  }
  .ag-row-hover {
    @apply cursor-pointer;
  }
  [class*='ag-theme-'] {
    --ag-selected-row-background-color: theme(colors.neutral.background);
  }
  .ag-header-cell.border-end::before,
  .ag-header-group-cell:not(
      .ag-header-span-height.ag-header-group-cell-no-group
    )::before {
    @apply hidden;
  }
}

.bitts-table-pro__zebra:deep() {
  .ag-row-odd,
  .ag-row-even {
    @apply border-none;
  }

  .ag-row-even {
    @apply bg-neutral-background-weak;
    &.ag-row-selected::before {
      @apply bg-neutral-background-weak rounded-16;
    }
  }
}
.bitts-table-pro__empty:deep() {
  @apply h-[500px];
  .ag-root {
    @apply h-[500px];
  }
  .ag-body-horizontal-scroll,
  .ag-paging-panel {
    @apply hidden;
  }
}

.bitts-table-pro__sticky-pagination:deep() {
  .ag-root,
  .ag-root-wrapper {
    display: unset;
  }
  .ag-header {
    @apply border-t border-solid border-neutral-border;
  }
  .ag-paging-panel {
    @apply sticky bottom-0 bg-white border-b border-solid border-neutral-border;
  }
}

.bitts-table-pro__sticky-headers:deep() {
  .ag-body-viewport {
    @apply overflow-y-auto;
    max-height: calc(100vh - v-bind(stickyHeaderOffsetHeight));
    scrollbar-width: thin !important; /* Firefox */
  }
  .ag-body-viewport::-webkit-scrollbar {
    width: 12px !important; /* Chrome, Safari, Edge */
  }
  .ag-body-viewport::-webkit-scrollbar-track {
    background: transparent;
  }
  .ag-body-viewport::-webkit-scrollbar-thumb {
    background-color: theme(colors.neutral.border);
    border-radius: 6px;
    border: 3px solid transparent;
    background-clip: content-box;
  }
}

.bitts-table-pro__fill-height {
  @apply flex flex-col h-full;

  .bitts-table-pro__ag-grid-container {
    @apply flex-1 min-h-0;
  }

  .bitts-table-pro__ag-grid-container > .ag-theme-balham {
    @apply h-full;
  }
}

.bitts-table-pro__no-page-size-selector:deep() {
  .ag-paging-page-size-selector,
  .ag-paging-page-size {
    @apply hidden;
  }
}

/* Checkboxes - match BittsCheckbox styling */
.bitts-table-pro:deep() {
  [class*='ag-theme-'] {
    --ag-checkbox-background-color: theme(colors.white);
    --ag-checkbox-unchecked-color: theme(colors.neutral.border-strong);
    --ag-checkbox-checked-color: theme(colors.white);
    --ag-checkbox-indeterminate-color: theme(colors.white);
    --ag-checkbox-border-radius: 3px;
  }

  .ag-checkbox-input-wrapper {
    input {
      @apply cursor-pointer;
    }

    &:focus-within {
      @apply shadow-none border-neutral-border-focus rounded-[3px];
    }

    &:not(.ag-checked):hover::after {
      @apply text-secondary-background-medium;
    }
  }

  .ag-checkbox-input-wrapper.ag-checked {
    @apply border-none rounded-[3px] bg-secondary-background-medium!;
  }

  .ag-checkbox-input-wrapper.ag-indeterminate {
    @apply border border-solid rounded-[3px] border-secondary-background-medium;

    &::after {
      /* Hide the default indeterminate icon */
      content: '' !important;
    }

    /* Create custom indeterminate indicator */
    &::before {
      @apply absolute top-1/2 left-1/2 w-8 h-8 bg-secondary-background-medium rounded-[2px];
      content: '';
      transform: translate(-50%, -50%);
    }
  }
}
</style>
